import { type Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';

export const questionSetPasteFilterPluginKey = new PluginKey('questionSetPasteFilter');

const BLOCKED_TOP_LEVEL_ACTIVITY_TYPES = new Set(['multipleChoice', 'shortAnswer', 'matching', 'detectAnswer']);

function activityChildConflictsQuestionType(node: PMNode): boolean {
    const declared = node.attrs.questionType;
    if (declared == null || declared === '') {
        return false;
    }
    for (let i = 0; i < node.childCount; i++) {
        const child = node.child(i);
        const name = child.type.name;
        if ((name === 'multipleChoice' || name === 'shortAnswer' || name === 'matching' || name === 'detectAnswer') && name !== declared) {
            return true;
        }
    }
    return false;
}

/**
 * Checks if the document has a blocked activity top level child.
 * @param doc - The document to check.
 */
function docHasBlockedActivityAsDirectChild(doc: PMNode): boolean {
    for (let i = 0; i < doc.childCount; i++) {
        if (BLOCKED_TOP_LEVEL_ACTIVITY_TYPES.has(doc.child(i).type.name)) {
            return true;
        }
    }
    return false;
}

/**
 * Rejects **paste** transactions (prosemirror-view sets `paste` meta) when:
 * - The slice would leave `multipleChoice` / `shortAnswer` / `matching` as **direct children of `doc`**, or
 * - The slice would place those nodes inside a `questionSet` whose non-empty `questionType` does not
 *   match that activity.
 *
 * For multi-node pastes whose last top-level node is a blocked activity, `transformPasted` first
 * unwraps that node to its inner children before the transaction is applied, so the filter above
 * never fires for that case.
 */
export function createQuestionSetPasteFilterPlugin() {
    return new Plugin({
        key: questionSetPasteFilterPluginKey,

        filterTransaction(tr: Transaction) {
            if (!tr.docChanged) {
                return true;
            }
            if (tr.getMeta('paste') !== true) {
                return true;
            }
            if (docHasBlockedActivityAsDirectChild(tr.doc)) {
                return false;
            }
            let blocked = false;
            tr.doc.descendants(node => {
                if (node.type.name !== 'questionSet') {
                    return;
                }
                if (activityChildConflictsQuestionType(node)) {
                    blocked = true;
                    return false;
                }
            });
            return !blocked;
        },
    });
}
